import { useState, useEffect, useContext, useRef } from "react";
import { GlobalContext } from "../../../Context/GlobalContext";
import useProfileDashboard from "../../../services/Settings/useProfileDashboard";
import usePosOrders from "../../../services/Pos/usePosOrders";
import useDining from "../../../services/Order/useDining";

export default function SelectPaymentModal({
  isOpen,
  onClose,
  onConfirm,
  cart,
  orderId,
  customerMobile,
  grandTotal,
  tenderedAmount,
  orderType,
  selectedTable,
  selectedRoom,
  placePosOrder,
  deliveryCharges = 0,
  deliveryChargesDeductedFrom = "customer",
  deliveryPartner = "",
}) {
  const { fetchReceiptDetails } = useProfileDashboard();
  const { refreshDiningCache } = useDining();
  const { getOrders, processPrintOrder } = usePosOrders();
  const { userID, user } = useContext(GlobalContext);

  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loading, setLoading] = useState(false);
  const [placing, setPlacing] = useState(false);
  const [selected, setSelected] = useState(null);
  const [refNumber, setRefNumber] = useState("");
  const [error, setError] = useState("");

  // Synchronous locks: React state alone cannot stop rapid clicks
  // before the next render.
  const placingLockRef = useRef(false);
  const paymentCompletedRef = useRef(false);

  useEffect(() => {
    if (!isOpen) return;

    placingLockRef.current = false;
    paymentCompletedRef.current = false;
    setPlacing(false);
    setSelected(null);
    setRefNumber("");
    setError("");

    const loadMethods = async () => {
      setLoading(true);

      try {
        const res = await fetchReceiptDetails();

        if (res?.responseCode === "00") {
          setPaymentMethods(
            (res.paymentMethods || []).filter(
              (method) => method.status === "active",
            ),
          );
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    loadMethods();
  }, [isOpen]);

  if (!isOpen) return null;

  const getTableNo = () => {
    if (normalizedType === "dining") {
      return (
        selectedTable?.tableNo ||
        selectedTable?.table_no ||
        selectedTable?.table ||
        selectedTable?.name ||
        ""
      );
    }

    if (normalizedType === "room_order") {
      return (
        selectedRoom?.roomNumber ||
        selectedRoom?.roomNo ||
        selectedRoom?.room_no ||
        selectedTable?.roomNumber ||
        selectedTable?.roomNo ||
        selectedTable?.room_no ||
        selectedTable?.tableNo ||
        selectedTable?.table_no ||
        ""
      );
    }

    return "";
  };

  const validate = () => {
    if (!selected) {
      setError("Please select a payment method");
      return false;
    }

    const enteredAmount = Number(tenderedAmount);

    if (!Number.isFinite(enteredAmount) || enteredAmount <= 0) {
      setError("Please enter valid tendered amount");
      return false;
    }

    if (!refNumber.trim()) {
      setError("Reference number is required");
      return false;
    }

    setError("");
    return true;
  };

  const normalizeOrderType = (value = "") => {
    const normalized = String(value)
      .trim()
      .toLowerCase()
      .replace(/[\s-]+/g, "_");

    if (["dining", "dinein", "dine_in"].includes(normalized)) {
      return "dining";
    }

    if (["takeaway", "takeway", "take_away"].includes(normalized)) {
      return "takeaway";
    }

    if (["homedelivery", "home_delivery"].includes(normalized)) {
      return "home_delivery";
    }

    if (["room_order", "roomorder"].includes(normalized)) {
      return "room_order";
    }

    return normalized;
  };

  const normalizedType = normalizeOrderType(orderType);

  const handleOkay = async () => {
    // This ref changes synchronously, so a second click in the same render
    // is blocked before any API work starts.
    if (placingLockRef.current || paymentCompletedRef.current) {
      console.warn("Duplicate OTHER payment click blocked");
      return;
    }

    placingLockRef.current = true;

    if (!validate()) {
      placingLockRef.current = false;
      return;
    }

    if (!orderId) {
      setError("Invalid Order ID");
      placingLockRef.current = false;
      return;
    }

    setPlacing(true);

    let paymentSucceeded = false;

    try {
      const tableNo =
        getTableNo() || selectedTable?.tableNo || selectedTable?.id || "";

      const rawMobile =
        typeof customerMobile === "object"
          ? customerMobile.mobile
          : customerMobile;

      const cleanMobile = String(rawMobile || "9999999999").replace(/\D/g, "");
      const finalMobile =
        cleanMobile.length === 10 ? `91${cleanMobile}` : cleanMobile;

      let sessionUser = {};

      try {
        sessionUser = JSON.parse(sessionStorage.getItem("user") || "{}");
      } catch (error) {
        console.error("Session user parse error:", error);
        sessionUser = {};
      }

      const finalUserId =
        userID ||
        user?.userid ||
        user?.userId ||
        user?.fk_userid ||
        sessionUser?.userid ||
        sessionUser?.userId ||
        sessionUser?.fk_userid ||
        sessionUser?.id ||
        "";

      if (!finalUserId) {
        console.error("USER ID MISSING DEBUG:", {
          contextUserID: userID,
          contextUser: user,
          sessionUser,
        });

        setError("User ID missing. Please login again.");
        return;
      }

      const finalDeliveryAmount = Number(deliveryCharges || 0);

      const finalDeliveryDeductedFrom =
        finalDeliveryAmount > 0
          ? deliveryChargesDeductedFrom || "customer"
          : "restaurant";

      const finalDeliveryPartner =
        finalDeliveryAmount > 0 ? deliveryPartner || "packky" : "";

      const payload = {
        userid: finalUserId,
        currency: "INR",
        custMobile: normalizedType === "dining" ? "" : finalMobile,
        custName: "",
        orderId,
        orderType: normalizedType,
        orderStatus: "completed",
        paymentMode: selected.method,
        receivedAmount: Number(tenderedAmount),
        tableNo,
        deliveryAmount: String(finalDeliveryAmount),
        deliveryChargesDeductedFrom: finalDeliveryDeductedFrom,
        deliveryPartner: finalDeliveryPartner,

        ...(selected.method !== "CASH" && {
          referenceNumber: refNumber.trim(),
        }),

        items: cart.map((c) => ({
          productId: String(c.item_id),
          sizeId: String(c.size_id || c.sizeId || "1"),
          productName: c.item_name,
          actualName: c.item_name,
          itemPrice: String(c.price),
          quantity: String(c.qty),
          customization: "[]",
          hasVarient: "false",
          itemImage: c.image || c.photo || "",
          sizeWords: c.size || "",
        })),
      };

      const res = await placePosOrder(payload);

      if (res?.duplicateBlocked || res?.responseCode === "DUPLICATE_BLOCKED") {
        console.warn("Duplicate OTHER payment API request ignored");
        return;
      }

      if (!res) {
        throw new Error("Order failed");
      }

      if (res?.responseCode !== "00") {
        throw new Error(res?.responseMessage || "Order failed");
      }

      // Keep both locks permanently active after API success.
      // They are reset only when this modal is opened for a new payment.
      paymentSucceeded = true;
      paymentCompletedRef.current = true;

      if (finalDeliveryAmount > 0) {
        sessionStorage.setItem(
          `pos_delivery_charge_${orderId}`,
          JSON.stringify({
            orderId,
            deliveryAmount: finalDeliveryAmount,
            deliveryChargesDeductedFrom: finalDeliveryDeductedFrom,
            deliveryPartner: finalDeliveryPartner,
          }),
        );
      }

      await processPrintOrder(orderId);

      const receiptData = await fetchReceiptDetails();

      if (normalizedType === "dining" || normalizedType === "room_order") {
        await refreshDiningCache();

        setTimeout(() => {
          refreshDiningCache();
        }, 700);
      }

      await getOrders({
        fromDate: new Date().toISOString().slice(0, 10),
        toDate: new Date().toISOString().slice(0, 10),
      });

      await onConfirm({
        method: selected.method,
        refNumber: refNumber.trim(),
        paidAmount: Number(tenderedAmount),
        receivedAmount: Number(tenderedAmount),
        orderData: res.response?.[0],
        receiptData,
      });
    } catch (err) {
      console.error("FLOW ERROR:", err);
      setError(err?.message || "Order flow failed. Please try again.");
    } finally {
      if (!paymentSucceeded) {
        placingLockRef.current = false;
        paymentCompletedRef.current = false;
        setPlacing(false);
      }
    }
  };

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center montserrat-font-1 justify-center bg-black/40 p-4">
        <div className="bg-white rounded-2xl flex montserrat-font-1 overflow-hidden w-full max-w-xl shadow-xl">
          {/* ── Left: Payment list ── */}
          <div className="flex-1 p-6 border-r-2 border-dashed border-gray-200 overflow-y-auto max-h-[80vh]">
            <h2 className="text-lg font-semibold montserrat-font-1  text-gray-900 text-center mb-4">
              Select Payment Method
            </h2>

            {loading ? (
              <div className="flex justify-center pt-10">
                <div className="w-6 h-6 border-2 border-indigo-400 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : paymentMethods.length === 0 ? (
              <p className="text-center text-sm text-gray-400 pt-10">
                No payment methods found
              </p>
            ) : (
              <div className="flex flex-col gap-2.5 montserrat-font-1">
                {paymentMethods.map((method) => (
                  <button
                    key={method.id}
                    type="button"
                    disabled={placing}
                    onClick={() => {
                      setSelected(method);
                      setRefNumber("");
                      setError("");
                    }}
                    className={`w-full text-left px-4 py-3.5 rounded-xl border text-sm font-semibold
                    transition-all shadow-sm ${
                      placing
                        ? "cursor-not-allowed opacity-60"
                        : "active:scale-95"
                    }
                    ${
                      selected?.id === method.id
                        ? "border-indigo-400 bg-indigo-50 text-indigo-700"
                        : "border-gray-200 bg-white text-gray-800 hover:border-indigo-300 hover:bg-indigo-50"
                    }`}
                  >
                    {method.method}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* ── Right: Reference + Actions ── */}
          <div className="w-52 flex-shrink-0 flex flex-col gap-3 p-4 pt-10 montserrat-font-1">
            <div>
              <p className="text-xs font-semibold text-gray-600 mb-1.5">
                Reference Number
              </p>

              <input
                type="text"
                value={refNumber}
                onChange={(e) => {
                  setRefNumber(e.target.value);
                  setError("");
                }}
                disabled={!selected || placing}
                placeholder={
                  selected ? "Enter reference number" : "Select a payment"
                }
                className={`w-full px-3 py-2.5 rounded-lg border text-sm outline-none transition-all ${
                  selected
                    ? "border-gray-300 bg-white text-gray-900 focus:border-indigo-400"
                    : "border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed"
                }`}
              />

              {error && (
                <p className="text-red-500 text-[11px] mt-1.5 leading-snug">
                  {error}
                </p>
              )}
            </div>

            <button
              type="button"
              onClick={handleOkay}
              disabled={!selected || !refNumber.trim() || placing}
              className={`w-full py-3 rounded-lg text-sm font-semibold tracking-widest transition-all active:scale-95 ${
                selected && refNumber.trim() && !placing
                  ? "bg-indigo-500 hover:bg-indigo-600 text-white border border-indigo-500"
                  : "bg-gray-100 text-gray-400 border border-gray-200 cursor-not-allowed"
              }`}
            >
              {placing ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Placing...
                </span>
              ) : (
                "OKAY"
              )}
            </button>

            <button
              type="button"
              onClick={() => {
                if (placingLockRef.current || placing) return;
                onClose?.();
              }}
              disabled={placing}
              className="w-full py-3 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 active:scale-95 text-sm font-bold tracking-widest text-gray-700 transition-all disabled:cursor-not-allowed disabled:opacity-50"
            >
              CLOSE
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
